import { Controller, useForm } from 'react-hook-form';
import greyDialogTitleSx from '../../components/Dialog/greyDialogTitleSx';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, TextField, Typography
} from '@mui/material';
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useState } from 'react';
import { updateUser } from '../../store/userThunk';
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { ApiStatus } from "../../utils/api.types";
import { IUserFormInputs } from "../../utils/user.types";
import '../../components/Invoice/Invoice.css'; // provides the "c-label" / "c-text-field-4" field styles

const inputSx = { width: '100%', mt: 0.5, borderRadius: '8px!important' };

interface IUserPasswordInputs {
  password: string;
}

export interface IUserPasswordDialogProps {
  open: boolean;
  onClose: () => void;
  user: any;
}

const schema = yup.object().shape({
  password: yup.string().min(8, 'Password must be at least 8 characters.').required('Password is required.'),
});

export default function UserPasswordDialog({ open, onClose, user }: IUserPasswordDialogProps) {
  const dispatch = useAppDispatch();
  const { selectedAccount } = useAppSelector((state => state.auth));
  const { status } = useAppSelector((state) => state.users);
  const isEmailVerified = !!user?.isEmailVerified;
  const [showPassword, setShowPassword] = useState(false);

  const methods = useForm<IUserPasswordInputs>({
    resolver: yupResolver(schema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: { password: '' },
  });

  const handleClose = () => {
    methods.reset({ password: '' });
    onClose();
  };

  const onSubmit = (sendPasswordEmail: boolean) => async (data: IUserPasswordInputs) => {
    try {
      await dispatch(updateUser({
        id: user._id,
        userUpdatePayload: {
          password: data.password,
          sendPasswordEmail,
          accounts: [selectedAccount._id],
        } as IUserFormInputs,
      })).unwrap();
      handleClose();
    } catch (error: any) {
      // Empty catch block.
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={greyDialogTitleSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h5">Change Password</Typography>
            <Typography variant="body2">Set a new password for {user?.name || 'this member'}</Typography>
          </Box>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container sx={{ pt: 3 }}>
          <Grid item xs={12} sm={6}>
            <h5 className='c-label'>New Password *</h5>
            <Controller
              name='password'
              control={methods.control}
              render={({ field, fieldState }) => (
                <>
                  <TextField
                    {...field}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    variant="standard"
                    size="small"
                    className='c-text-field-4'
                    sx={inputSx}
                    autoComplete="new-password"
                    InputProps={{
                      disableUnderline: true,
                      endAdornment: (
                        <IconButton size="small" edge="end" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      )
                    }}
                  />
                  {fieldState.error && <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{fieldState.error.message}</p>}
                </>
              )}
            />
          </Grid>

          {!isEmailVerified && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="body2" color="error">
                This member’s email is not verified, so the password can’t be emailed. Share it with them yourself.
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button variant='outlined' onClick={handleClose}>
          Cancel
        </Button>
        {/* Plain Button: the theme paints any LoadingButton in DialogActions solid green, so an outlined one is unreadable. */}
        <Button
          onClick={methods.handleSubmit(onSubmit(false))}
          disabled={status === ApiStatus.LOADING}
          variant="outlined"
        >
          Update
        </Button>
        <LoadingButton
          onClick={methods.handleSubmit(onSubmit(true))}
          loading={status === ApiStatus.LOADING}
          variant="contained"
          disabled={!isEmailVerified}
        >
          Update &amp; Email
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
